import {
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu as AntMenu,
  message,
  Modal,
  Popconfirm,
  Result,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LogoutOutlined,
  MenuOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createUser,
  createMenu,
  deleteUser,
  deleteMenu,
  getRoleMenuPermissions,
  listMenuPermissions,
  listMenus,
  listUserTypes,
  listUsers,
  saveRoleMenuPermissions,
  updateUser,
  updateMenu,
} from '../../services/adminCoreApi';
import { getStoredUser, logout } from '../../services/authApi';
import * as AdminMenuComponents from './menuComponents';
import './AdminDashboard.css';

const { Content, Header, Sider } = Layout;
const { Text, Title } = Typography;

function getMenuComponent(componentName) {
  const Component = AdminMenuComponents[componentName];
  return typeof Component === 'function' ? Component : null;
}

function normalizeMenuPath(path) {
  if (!path || path === '/') {
    return '/';
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return normalizedPath.replace(/\/$/, '') || '/';
}

function getMenuId(menu) {
  return menu.menu_id ?? menu.menuId ?? menu.MenuID;
}

function getParentMenuId(menu) {
  return menu.parent_menu_id ?? menu.parentMenuId ?? menu.ParentMenuID ?? null;
}

function getMenuItemKey(menu) {
  return String(menu.menu_key || getMenuId(menu));
}

function sortMenusByOrder(left, right) {
  return Number(left.display_order ?? left.displayOrder ?? 0) - Number(right.display_order ?? right.displayOrder ?? 0);
}

function normalizeRoleName(value) {
  return String(value ?? '').trim().replace(/[\s_-]+/g, ' ').toUpperCase();
}

function isSuperAdminUserType(userType) {
  const userTypeId = userType?.user_type_id ?? userType?.userTypeId ?? userType?.id;
  const userTypeName = userType?.user_type_name ?? userType?.userTypeName ?? userType?.name;

  return Number(userTypeId) === 1 || normalizeRoleName(userTypeName) === 'SUPER ADMIN';
}

function buildSidebarMenuItems(menus, onMenuLabelClick) {
  const menuById = new Map();
  const childrenByParentId = new Map();

  menus.forEach((menu) => {
    const menuId = getMenuId(menu);
    const parentMenuId = getParentMenuId(menu);

    if (menuId !== undefined && menuId !== null) {
      menuById.set(Number(menuId), menu);
    }

    if (parentMenuId !== undefined && parentMenuId !== null) {
      const key = Number(parentMenuId);
      childrenByParentId.set(key, [...(childrenByParentId.get(key) || []), menu]);
    }
  });

  function toMenuItem(menu) {
    const menuId = getMenuId(menu);
    const children = (childrenByParentId.get(Number(menuId)) || [])
      .slice()
      .sort(sortMenusByOrder)
      .map(toMenuItem);
    const hasChildren = children.length > 0;

    return {
      key: getMenuItemKey(menu),
      icon: <MenuOutlined />,
      label: hasChildren && menu.api_url ? (
        <button
          type="button"
          className="admin-sidebar-menu-link"
          onClick={(event) => {
            event.stopPropagation();
            onMenuLabelClick(menu);
          }}
        >
          {menu.menu_name}
        </button>
      ) : menu.menu_name,
      children: hasChildren ? children : undefined,
    };
  }

  return menus
    .filter((menu) => {
      const parentMenuId = getParentMenuId(menu);
      return parentMenuId === undefined || parentMenuId === null || !menuById.has(Number(parentMenuId));
    })
    .slice()
    .sort(sortMenusByOrder)
    .map(toMenuItem);
}

function getSidebarOpenKeys(menu, menus) {
  const openKeys = [];
  const menuById = new Map(menus.map((item) => [Number(getMenuId(item)), item]));
  let parentMenuId = getParentMenuId(menu);

  while (parentMenuId !== undefined && parentMenuId !== null) {
    const parentMenu = menuById.get(Number(parentMenuId));
    if (!parentMenu) {
      break;
    }
    openKeys.unshift(getMenuItemKey(parentMenu));
    parentMenuId = getParentMenuId(parentMenu);
  }

  return openKeys;
}

function getSidebarParentOpenKeys(menus) {
  const parentIds = new Set(
    menus
      .map(getParentMenuId)
      .filter((parentMenuId) => parentMenuId !== undefined && parentMenuId !== null)
      .map(Number),
  );

  return menus
    .filter((menu) => parentIds.has(Number(getMenuId(menu))))
    .map(getMenuItemKey);
}

function MenuRouteView({ menu }) {
  const componentName = menu.component_name || '';
  const Component = componentName ? getMenuComponent(componentName) : null;

  return (
    <section className="admin-menu-route">
      <div className="admin-section-head">
        <div>
          <Title level={4}>{menu.menu_name}</Title>
          <Text type="secondary">{menu.api_url}</Text>
        </div>
        <Tag>{componentName || 'No component'}</Tag>
      </div>
      {Component ? (
        <Component menu={menu} />
      ) : (
        <Result
          status="info"
          title="Component not matched"
          subTitle={
            componentName
              ? `${componentName} is not available in the admin component registry.`
              : 'Add a component_name in the menu table to load this menu route.'
          }
        />
      )}
    </section>
  );
}

function getPermissionRows(groups) {
  return groups.flatMap((menu) => {
    const menuId = menu.menu_id ?? menu.menuId ?? menu.MenuID;
    const menuName = menu.menu_name ?? menu.menuName ?? menu.MenuName ?? `Menu ${menuId ?? ''}`;
    const menuKey = menu.menu_key ?? menu.menuKey ?? menu.MenuKey ?? '';
    const permissions = menu.permissions ?? menu.Permissions ?? {};

    return Object.entries(permissions).map(([permissionMapKey, permission]) => {
      const permissionId = permission.permission_id ?? permission.permissionId ?? permission.PermissionID;
      const permissionKey = permission.permission_key
        ?? permission.permissionKey
        ?? permission.PermissionKey
        ?? permissionMapKey;
      const rawValue = permission.value ?? permission.Value ?? permission.access_value ?? permission.accessValue ?? permission;

      return {
        key: `${menuId}-${permissionId ?? permissionKey}`,
        menuId,
        menuName,
        menuKey,
        permissionId,
        permissionKey,
        accessValue: Number(rawValue) === 1 ? 1 : 0,
      };
    });
  });
}

function PermissionEditor({
  title,
  description,
  selectedName,
  loading,
  saving,
  rows,
  onChange,
  onSave,
}) {
  const columns = [
    {
      title: 'Menu',
      dataIndex: 'menuName',
      width: 220,
      render: (value, row) => (
        <div className="admin-menu-cell">
          <span>{value}</span>
          <Text type="secondary">{row.menuKey}</Text>
        </div>
      ),
    },
    {
      title: 'Permission',
      dataIndex: 'permissionKey',
      width: 160,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: 'Access',
      dataIndex: 'accessValue',
      width: 140,
      render: (value, row) => (
        <Segmented
          size="small"
          value={value}
          options={[
            { label: 'Off', value: 0 },
            { label: 'On', value: 1 },
          ]}
          onChange={(nextValue) => onChange(row.key, nextValue)}
        />
      ),
    },
  ];

  return (
    <section className="admin-editor">
      <div className="admin-section-head">
        <div>
          <Title level={4}>{title}</Title>
          <Text type="secondary">{selectedName || description}</Text>
        </div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!selectedName || rows.length === 0}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
      {loading ? (
        <div className="admin-loading">
          <Spin />
        </div>
      ) : rows.length ? (
        <Table
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ y: 420 }}
        />
      ) : (
        <Empty description="Select a record to customize permissions" />
      )}
    </section>
  );
}

function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [permissionMaster, setPermissionMaster] = useState([]);
  const [selectedUserTypeId, setSelectedUserTypeId] = useState(null);
  const [rolePermissionRows, setRolePermissionRows] = useState([]);
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuSaving, setMenuSaving] = useState(false);
  const [menuDeletingId, setMenuDeletingId] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userDeletingId, setUserDeletingId] = useState(null);
  const [sidebarOpenKeys, setSidebarOpenKeys] = useState([]);
  const currentUser = getStoredUser();
  const sidebarMenus = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('menus') || '[]');
    } catch {
      return [];
    }
  }, []);
  const activeMenuPath = normalizeMenuPath(
    location.pathname.startsWith('/admin')
      ? location.pathname.replace(/^\/admin/, '') || '/'
      : location.pathname,
  );
  const selectedRouteMenu = sidebarMenus.find((menu) => (
    normalizeMenuPath(menu.api_url) === activeMenuPath
  ));
  const sidebarSelectedKey = selectedRouteMenu ? getMenuItemKey(selectedRouteMenu) : null;
  const sidebarMenuItems = useMemo(
    () => buildSidebarMenuItems(sidebarMenus, navigateSidebarMenu),
    [sidebarMenus],
  );
  const defaultSidebarOpenKeys = useMemo(
    () => getSidebarParentOpenKeys(sidebarMenus),
    [sidebarMenus],
  );

  async function loadData() {
    setLoading(true);
    try {
      const [usersData, userTypesData, menusData, permissionsData] = await Promise.all([
        listUsers(),
        listUserTypes(),
        listMenus(),
        listMenuPermissions(),
      ]);
      setUsers(usersData ?? []);
      setUserTypes(userTypesData ?? []);
      setMenus(menusData ?? []);
      setPermissionMaster(permissionsData ?? []);
      setSelectedUserTypeId((userTypesData ?? [])[0]?.user_type_id ?? null);
    } catch (error) {
      message.error(error.message || 'Unable to load admin data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setSidebarOpenKeys((currentOpenKeys) => {
      const selectedParentKeys = selectedRouteMenu ? getSidebarOpenKeys(selectedRouteMenu, sidebarMenus) : [];
      const existingValidKeys = currentOpenKeys.filter((key) => defaultSidebarOpenKeys.includes(key));
      const nextOpenKeys = currentOpenKeys.length
        ? [...existingValidKeys, ...selectedParentKeys]
        : [...defaultSidebarOpenKeys, ...selectedParentKeys];

      return Array.from(new Set(nextOpenKeys));
    });
  }, [defaultSidebarOpenKeys, selectedRouteMenu, sidebarMenus]);

  useEffect(() => {
    if (!selectedUserTypeId) {
      setRolePermissionRows([]);
      return;
    }
    setPermissionLoading(true);
    getRoleMenuPermissions(selectedUserTypeId)
      .then((groups) => setRolePermissionRows(getPermissionRows(groups ?? [])))
      .catch((error) => message.error(error.message || 'Unable to load role permissions'))
      .finally(() => setPermissionLoading(false));
  }, [selectedUserTypeId]);

  const selectedUserType = useMemo(
    () => userTypes.find((userType) => userType.user_type_id === selectedUserTypeId),
    [selectedUserTypeId, userTypes],
  );

  const userTypeNameById = useMemo(() => (
    userTypes.reduce((lookup, userType) => ({
      ...lookup,
      [userType.user_type_id]: userType.user_type_name,
    }), {})
  ), [userTypes]);

  const manageableUserTypes = useMemo(
    () => userTypes.filter((userType) => !isSuperAdminUserType(userType)),
    [userTypes],
  );

  const manageableUsers = useMemo(
    () => users.filter((user) => !isSuperAdminUserType({
      user_type_id: user.user_type_id,
      user_type_name: userTypeNameById[user.user_type_id],
    })),
    [userTypeNameById, users],
  );

  const metrics = [
    { label: 'Users', value: manageableUsers.length },
    { label: 'User Types', value: userTypes.length },
    { label: 'Menus', value: menus.length },
    { label: 'Permissions', value: permissionMaster.length },
  ];

  function updateRolePermission(key, value) {
    setRolePermissionRows((rows) => rows.map((row) => (
      row.key === key ? { ...row, accessValue: value } : row
    )));
  }

  async function saveRolePermissions() {
    setSaving(true);
    try {
      await saveRoleMenuPermissions(selectedUserTypeId, rolePermissionRows.map((row) => ({
        menu_id: row.menuId,
        permission_id: row.permissionId,
        access_value: row.accessValue,
        updated_by: currentUser?.userId ?? null,
      })));
      message.success('Role permissions updated');
    } catch (error) {
      message.error(error.message || 'Unable to save role permissions');
    } finally {
      setSaving(false);
    }
  }

  function handleLogout() {
    logout();
    onLogout?.();
  }

  function navigateSidebarMenu(menu) {
    if (!menu?.api_url) {
      return;
    }
    navigate(normalizeMenuPath(menu.api_url));
  }

  function handleSidebarMenuClick({ key }) {
    const menu = sidebarMenus.find((item) => getMenuItemKey(item) === String(key));
    navigateSidebarMenu(menu);
  }

  function openCreateMenu() {
    setEditingMenu(null);
    menuForm.resetFields();
    menuForm.setFieldsValue({
      status: 'ACTIVE',
      display_order: 0,
      menu_type: 'MENU',
    });
    setMenuModalOpen(true);
  }

  function openEditMenu(menu) {
    setEditingMenu(menu);
    menuForm.setFieldsValue({
      menu_name: menu.menu_name,
      menu_key: menu.menu_key,
      api_url: menu.api_url,
      menu_type: menu.menu_type,
      parent_menu_id: menu.parent_menu_id,
      component_name: menu.component_name,
      icon: menu.icon,
      display_order: menu.display_order,
      status: menu.status,
    });
    setMenuModalOpen(true);
  }

  async function submitMenu(values) {
    setMenuSaving(true);
    try {
      const payload = {
        ...values,
        parent_menu_id: values.parent_menu_id || null,
        display_order: Number(values.display_order ?? 0),
        updated_by: currentUser?.userId ?? null,
        created_by: currentUser?.userId ?? null,
      };
      if (editingMenu) {
        await updateMenu(editingMenu.menu_id, payload);
        message.success('Menu updated');
      } else {
        await createMenu(payload);
        message.success('Menu created');
      }
      setMenuModalOpen(false);
      setEditingMenu(null);
      await loadData();
    } catch (error) {
      message.error(error.message || 'Unable to save menu');
    } finally {
      setMenuSaving(false);
    }
  }

  async function handleDeleteMenu(menuId) {
    setMenuDeletingId(menuId);
    try {
      await deleteMenu(menuId);
      message.success('Menu deleted');
      await loadData();
    } catch (error) {
      message.error(error.message || 'Unable to delete menu');
    } finally {
      setMenuDeletingId(null);
    }
  }

  function openCreateUser() {
    setEditingUser(null);
    userForm.resetFields();
    userForm.setFieldsValue({
      status: 'ACTIVE',
      user_type_id: manageableUserTypes[0]?.user_type_id,
    });
    setUserModalOpen(true);
  }

  function openEditUser(user) {
    setEditingUser(user);
    userForm.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      username: user.username,
      email: user.email,
      mobile: user.mobile,
      user_type_id: user.user_type_id,
      profile_image: user.profile_image,
      status: user.status,
    });
    setUserModalOpen(true);
  }

  async function submitUser(values) {
    setUserSaving(true);
    try {
      const payload = {
        ...values,
        updated_by: currentUser?.userId ?? null,
        created_by: currentUser?.userId ?? null,
      };
      if (!payload.password) {
        delete payload.password;
      }
      if (editingUser) {
        await updateUser(editingUser.user_id, payload);
        message.success('User updated');
      } else {
        await createUser(payload);
        message.success('User created');
      }
      setUserModalOpen(false);
      setEditingUser(null);
      await loadData();
    } catch (error) {
      message.error(error.message || 'Unable to save user');
    } finally {
      setUserSaving(false);
    }
  }

  async function handleDeleteUser(userId) {
    setUserDeletingId(userId);
    try {
      await deleteUser(userId);
      message.success('User deleted');
      await loadData();
    } catch (error) {
      message.error(error.message || 'Unable to delete user');
    } finally {
      setUserDeletingId(null);
    }
  }

  function openRolePermissions(userType) {
    setSelectedUserTypeId(userType.user_type_id);
  }

  const userColumns = [
    { title: 'Name', dataIndex: 'first_name', render: (value, row) => `${value ?? ''} ${row.last_name ?? ''}`.trim() || row.username },
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'User Type',
      dataIndex: 'user_type_id',
      width: 160,
      render: (value) => userTypeNameById[value] || `Type ${value}`,
    },
    { title: 'Status', dataIndex: 'status', width: 110, render: (value) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 104,
      render: (_, row) => (
        <Space size={4}>
          <Button
            aria-label="Edit user"
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditUser(row)}
          />
          <Popconfirm
            title="Delete user?"
            description="This removes the user from the auth database."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteUser(row.user_id)}
          >
            <Button
              danger
              aria-label="Delete user"
              icon={<DeleteOutlined />}
              loading={userDeletingId === row.user_id}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const userTypeColumns = [
    { title: 'User Type', dataIndex: 'user_type_name' },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Status', dataIndex: 'status', width: 110, render: (value) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 96,
      render: (_, row) => (
        <Button
          aria-label="Edit role permissions"
          icon={<SafetyCertificateOutlined />}
          size="small"
          onClick={() => openRolePermissions(row)}
        >
          Permission
        </Button>
      ),
    },
  ];

  const menuColumns = [
    { title: 'Menu', dataIndex: 'menu_name' },
    { title: 'Key', dataIndex: 'menu_key' },
    { title: 'Type', dataIndex: 'menu_type', width: 120 },
    { title: 'Order', dataIndex: 'display_order', width: 90 },
    { title: 'Status', dataIndex: 'status', width: 110, render: (value) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 104,
      render: (_, row) => (
        <Space size={4}>
          <Button
            aria-label="Edit menu"
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditMenu(row)}
          />
          <Popconfirm
            title="Delete menu?"
            description="This removes the menu from the auth database."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteMenu(row.menu_id)}
          >
            <Button
              danger
              aria-label="Delete menu"
              icon={<DeleteOutlined />}
              loading={menuDeletingId === row.menu_id}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="admin-shell">
      <Sider width={248} className="admin-sider">
        <div className="admin-brand">
          <img src="/favicon.svg" alt="" />
          <div>
            <strong>Zinnext</strong>
            <span>Admin Core</span>
          </div>
        </div>
        <div className="admin-profile">
          <Text>{currentUser?.username || 'Admin'}</Text>
          <span>{currentUser?.email}</span>
        </div>
        <div className="admin-sidebar-nav">
          <Text className="admin-sidebar-title">Menus</Text>
          <AntMenu
            theme="dark"
            mode="inline"
            selectedKeys={sidebarSelectedKey ? [String(sidebarSelectedKey)] : []}
            openKeys={sidebarOpenKeys}
            onOpenChange={setSidebarOpenKeys}
            onClick={handleSidebarMenuClick}
            items={sidebarMenuItems}
          />
        </div>
      </Sider>
      <Layout>
        <Header className="admin-header">
          <div>
            <Title level={3}>Admin</Title>
            <Text type="secondary">Manage users, user types, menus, and access.</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadData}>Refresh</Button>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>Logout</Button>
          </Space>
        </Header>
        <Content className="admin-content">
          {selectedRouteMenu ? (
            <MenuRouteView menu={selectedRouteMenu} />
          ) : (
            <>
              <div className="admin-metrics">
                {metrics.map((item) => (
                  <div className="admin-metric" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'users',
                    label: <span><UserOutlined /> Users</span>,
                    children: (
                      <div className="admin-stack">
                        <section className="admin-section">
                          <div className="admin-section-head">
                            <div>
                              <Title level={4}>Users</Title>
                              <Text type="secondary">Create and manage users with non-super-admin user types.</Text>
                            </div>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateUser}>
                              Add
                            </Button>
                          </div>
                          <Table
                            loading={loading}
                            rowKey="user_id"
                            size="small"
                            columns={userColumns}
                            dataSource={manageableUsers}
                            pagination={{ pageSize: 8 }}
                          />
                        </section>
                      </div>
                    ),
                  },
                  {
                    key: 'roles',
                    label: <span><TeamOutlined /> User Types</span>,
                    children: (
                      <div className="admin-stack">
                        <section className="admin-section">
                          <div className="admin-section-head">
                            <div>
                              <Title level={4}>User Types</Title>
                              <Text type="secondary">Role level permissions are inherited by users.</Text>
                            </div>
                          </div>
                          <Table
                            loading={loading}
                            rowKey="user_type_id"
                            size="small"
                            columns={userTypeColumns}
                            dataSource={userTypes}
                            pagination={false}
                            rowClassName={(row) => row.user_type_id === selectedUserTypeId ? 'admin-selected-row' : ''}
                          />
                        </section>
                        <PermissionEditor
                          title="Role Permissions"
                          description="Pick a user type from the table"
                          selectedName={selectedUserType?.user_type_name}
                          loading={permissionLoading}
                          saving={saving}
                          rows={rolePermissionRows}
                          onChange={updateRolePermission}
                          onSave={saveRolePermissions}
                        />
                      </div>
                    ),
                  },
                  {
                    key: 'menus',
                    label: <span><MenuOutlined /> Menus</span>,
                    children: (
                      <div className="admin-stack">
                        <section className="admin-section">
                          <div className="admin-section-head">
                            <div>
                              <Title level={4}>Menus</Title>
                              <Text type="secondary">Available navigation items from the auth service.</Text>
                            </div>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateMenu}>
                              Add
                            </Button>
                          </div>
                          <Table
                            loading={loading}
                            rowKey="menu_id"
                            size="small"
                            columns={menuColumns}
                            dataSource={menus}
                            pagination={{ pageSize: 10 }}
                          />
                        </section>
                      </div>
                    ),
                  },
                ]}
              />
            </>
          )}
          <Modal
            title={editingUser ? 'Edit User' : 'Add User'}
            open={userModalOpen}
            onCancel={() => setUserModalOpen(false)}
            onOk={() => userForm.submit()}
            confirmLoading={userSaving}
            destroyOnHidden
          >
            <Form
              form={userForm}
              layout="vertical"
              requiredMark={false}
              onFinish={submitUser}
            >
              <div className="admin-form-row">
                <Form.Item
                  label="First Name"
                  name="first_name"
                  rules={[{ required: true, message: 'First name is required' }]}
                >
                  <Input placeholder="John" />
                </Form.Item>
                <Form.Item label="Last Name" name="last_name">
                  <Input placeholder="Doe" />
                </Form.Item>
              </div>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: 'Username is required' }]}
              >
                <Input placeholder="john.doe" />
              </Form.Item>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input placeholder="john@example.com" />
              </Form.Item>
              <Form.Item
                label={editingUser ? 'Password' : 'Password'}
                name="password"
                rules={editingUser ? [] : [{ required: true, message: 'Password is required' }]}
              >
                <Input.Password placeholder={editingUser ? 'Leave empty to keep current password' : 'Password'} />
              </Form.Item>
              <div className="admin-form-row">
                <Form.Item label="Mobile" name="mobile">
                  <Input placeholder="9999999999" />
                </Form.Item>
                <Form.Item
                  label="User Type"
                  name="user_type_id"
                  rules={[{ required: true, message: 'User type is required' }]}
                >
                  <Select
                    placeholder="Select user type"
                    options={manageableUserTypes.map((userType) => ({
                      label: userType.user_type_name,
                      value: userType.user_type_id,
                    }))}
                  />
                </Form.Item>
              </div>
              <Form.Item label="Profile Image" name="profile_image">
                <Input placeholder="https://..." />
              </Form.Item>
              <Form.Item label="Status" name="status">
                <Select
                  options={[
                    { label: 'ACTIVE', value: 'ACTIVE' },
                    { label: 'INACTIVE', value: 'INACTIVE' },
                  ]}
                />
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            title={editingMenu ? 'Edit Menu' : 'Add Menu'}
            open={menuModalOpen}
            onCancel={() => setMenuModalOpen(false)}
            onOk={() => menuForm.submit()}
            confirmLoading={menuSaving}
            destroyOnHidden
          >
            <Form
              form={menuForm}
              layout="vertical"
              requiredMark={false}
              onFinish={submitMenu}
            >
              <Form.Item
                label="Menu Name"
                name="menu_name"
                rules={[{ required: true, message: 'Menu name is required' }]}
              >
                <Input placeholder="Dashboard" />
              </Form.Item>
              <Form.Item
                label="Menu Key"
                name="menu_key"
                rules={[{ required: true, message: 'Menu key is required' }]}
              >
                <Input placeholder="dashboard" />
              </Form.Item>
              <Form.Item label="API URL" name="api_url">
                <Input placeholder="/dashboard" />
              </Form.Item>
              <Form.Item label="Menu Type" name="menu_type">
                <Input placeholder="MENU" />
              </Form.Item>
              <Form.Item label="Parent Menu" name="parent_menu_id">
                <Select
                  allowClear
                  placeholder="No parent"
                  options={menus
                    .filter((menu) => menu.menu_id !== editingMenu?.menu_id)
                    .map((menu) => ({
                      label: menu.menu_name,
                      value: menu.menu_id,
                    }))}
                />
              </Form.Item>
              <Form.Item label="Component Name" name="component_name">
                <Input placeholder="Dashboard" />
              </Form.Item>
              <Form.Item label="Icon" name="icon">
                <Input placeholder="dashboard" />
              </Form.Item>
              <div className="admin-form-row">
                <Form.Item label="Display Order" name="display_order">
                  <InputNumber min={0} precision={0} />
                </Form.Item>
                <Form.Item label="Status" name="status">
                  <Select
                    options={[
                      { label: 'ACTIVE', value: 'ACTIVE' },
                      { label: 'INACTIVE', value: 'INACTIVE' },
                    ]}
                  />
                </Form.Item>
              </div>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
}

export default AdminDashboard;
